import { CACHE_KEYS } from '../geolocation-detection/constants.js';
import { getCachedValue } from '../geolocation-detection/get-cached-value.js';
import { whenGeolocationReady } from '../geolocation-detection/get-when-ready.js';

/**
 * Maps data-gt-geo values (from PHP) to cache config.
 *
 * @type {Object<string, { key: string, expiryKey: string }>}
 */
const GEO_DATA_KEY_TO_CACHE = {
	city: CACHE_KEYS.city,
	state: CACHE_KEYS.state,
	city_slug: CACHE_KEYS.citySlug,
	state_slug: CACHE_KEYS.stateSlug,
};

/**
 * Prefills geolocation placeholders in the DOM with cached values.
 *
 * - City/state spans: prefix + location when `data-prefix` exists, otherwise location only.
 * - Slug spans: slug value only (no prefix).
 * - Plain-text tags: `{gt_geo_city}`, `{gt_geo_state}`, `{gt_geo_*_slug}` use value only.
 */
class PrefillGeolocationData {
	constructor() {
		this.initWhenDomReady();
	}

	/**
	 * Initializes prefill when the DOM and geolocation data are both ready.
	 * Waits for both in parallel so prefill runs as soon as the slower of the two is ready.
	 *
	 * @return {void}
	 */
	initWhenDomReady() {
		const domReady =
			document.readyState === 'loading'
				? new Promise((resolve) => {
						document.addEventListener('DOMContentLoaded', resolve, {
							once: true,
						});
					})
				: Promise.resolve();

		Promise.all([whenGeolocationReady(), domReady]).then(() => this.prefill());
	}

	/**
	 * Prefills geolocation placeholders in the DOM with cached values.
	 * Replaces in text content only. Search filter form and multi-step search filter form are handled separately.
	 *
	 * @return {void}
	 */
	prefill() {
		if (!document.body) {
			return;
		}

		this.replaceGeoSpans(document.body);

		const replacements = this.getReplacementMap();
		if (Object.keys(replacements).length === 0) {
			return;
		}

		this.replaceInTextNodes(document.body, replacements);
	}

	/**
	 * Fills `[data-gt-geo]` spans with cached location. City/state use prefix + value; slugs use value only.
	 *
	 * @param {Node} root Root to search under.
	 * @return {void}
	 */
	replaceGeoSpans(root) {
		const nodes = root.querySelectorAll('[data-gt-geo]');

		nodes.forEach((el) => {
			const geoKey = el.dataset.gtGeo;
			const cacheCfg = geoKey ? GEO_DATA_KEY_TO_CACHE[geoKey] : undefined;
			if (!cacheCfg) {
				return;
			}

			const location = getCachedValue(cacheCfg.key, cacheCfg.expiryKey);
			if (location === null) {
				return;
			}

			if (geoKey === 'city_slug' || geoKey === 'state_slug') {
				el.textContent = location;
				return;
			}

			const rawPrefix =
				el.dataset.prefix !== undefined && el.dataset.prefix !== ''
					? el.dataset.prefix
					: '';
			const prefix = String(rawPrefix).trim();
			el.textContent = prefix ? `${prefix} ${location}` : location;
		});
	}

	/**
	 * Builds a map of placeholder → replacement value for placeholders that have cached data.
	 *
	 * @return {Object<string, string>} Map of placeholder string to replacement value.
	 */
	getReplacementMap() {
		const city = getCachedValue(CACHE_KEYS.city.key, CACHE_KEYS.city.expiryKey);
		const state = getCachedValue(
			CACHE_KEYS.state.key,
			CACHE_KEYS.state.expiryKey,
		);
		const citySlug = getCachedValue(
			CACHE_KEYS.citySlug.key,
			CACHE_KEYS.citySlug.expiryKey,
		);
		const stateSlug = getCachedValue(
			CACHE_KEYS.stateSlug.key,
			CACHE_KEYS.stateSlug.expiryKey,
		);

		const map = {};
		if (city !== null) {
			map['{gt_geo_city}'] = city;
		}

		if (state !== null) {
			map['{gt_geo_state}'] = state;
		}

		if (citySlug !== null) {
			map['{gt_geo_city_slug}'] = citySlug;
		}

		if (stateSlug !== null) {
			map['{gt_geo_state_slug}'] = stateSlug;
		}

		return map;
	}

	/**
	 * Replaces placeholders in all text nodes under a root using TreeWalker.
	 * Avoids serializing the whole document; only touches text nodes that may contain placeholders.
	 *
	 * @param {Node}                   root         Root node to walk (e.g. document.body).
	 * @param {Object<string, string>} replacements Map of placeholder → value.
	 * @return {void}
	 */
	replaceInTextNodes(root, replacements) {
		const treeWalker = document.createTreeWalker(
			root,
			NodeFilter.SHOW_TEXT,
			null,
			false,
		);

		let node;
		while ((node = treeWalker.nextNode())) {
			const original = node.nodeValue;
			const replaced = this.replaceInText(original, replacements);
			if (replaced !== original) {
				node.nodeValue = replaced;
			}
		}
	}

	/**
	 * Replaces placeholder strings in a text string.
	 *
	 * @param {string}                 text         Original text.
	 * @param {Object<string, string>} replacements Map of placeholder → value.
	 * @return {string} Text with placeholders replaced.
	 */
	replaceInText(text, replacements) {
		let result = text;
		for (const [placeholder, value] of Object.entries(replacements)) {
			result = result.split(placeholder).join(value);
		}

		return result;
	}
}

new PrefillGeolocationData();
